import React, { useState, useEffect } from 'react';
import { APP_STORE_LINKS } from '../constants/appLinks';
import AppNav from '../components/app/AppNav';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);

const Download: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isIOS) window.location.href = APP_STORE_LINKS.APPLE_APP_STORE;
    else if (isAndroid) window.location.href = APP_STORE_LINKS.GOOGLE_PLAY;
  }, []);

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetch('https://dev.api.sahayy.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'App Launch Notification',
          email,
          subject: 'Notify me when Sahayy launches',
          message: 'Please notify me when the Sahayy app becomes available for download.',
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting notification request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <AppNav />

      {/* Hero */}
      <div className="bg-indigo-600 text-white px-6 py-16 text-center">
        <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-3">Download</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Download Sahayy</h1>
        <p className="text-indigo-200 text-lg">
          Join your community. Connect with neighbors.<br />
          Available on your favorite platform.
        </p>
      </div>

      <div className="max-w-lg mx-auto px-6 py-14 text-center">
        {/* Store links */}
        <div className="flex flex-col gap-4 mb-12">
          <a
            href={APP_STORE_LINKS.APPLE_APP_STORE}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 hover:border-indigo-200 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-4">
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1.6em" width="1.6em" className="text-gray-700" xmlns="http://www.w3.org/2000/svg">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              <div className="text-left">
                <div className="font-bold text-gray-900">App Store</div>
                <div className="text-xs text-gray-400">iPhone & iPad</div>
              </div>
            </div>
            <span className="text-indigo-600 font-bold text-sm">Download →</span>
          </a>

          <a
            href={APP_STORE_LINKS.GOOGLE_PLAY}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 hover:border-indigo-200 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-4">
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1.6em" width="1.6em" className="text-gray-700" xmlns="http://www.w3.org/2000/svg">
                <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
              </svg>
              <div className="text-left">
                <div className="font-bold text-gray-900">Google Play</div>
                <div className="text-xs text-gray-400">Android devices</div>
              </div>
            </div>
            <span className="text-indigo-600 font-bold text-sm">Download →</span>
          </a>
        </div>

        {/* Notify */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8">
          {isSubmitted ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center text-xl mx-auto mb-4">✓</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">You're on the list!</h3>
              <p className="text-gray-400 text-sm">We'll notify you as soon as Sahayy is available.</p>
            </div>
          ) : (
            <>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Get notified when we launch</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Be the first to know when Sahayy becomes available in your area.
              </p>
              <form onSubmit={handleNotifySubmit} className="flex flex-col gap-3">
                <input
                  type="email" placeholder="Enter your email"
                  value={email} onChange={e => setEmail(e.target.value)} required
                  className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                />
                <button
                  type="submit" disabled={isLoading}
                  className={`py-3 rounded-xl font-bold transition-colors ${isLoading ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500'}`}
                >
                  {isLoading ? 'Sending...' : 'Notify Me'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Download;
